// GRN phase 1 (2026-10-21): rules and labels shared by the purchase-path step, the direct purchase screen and the
// receive window. The server checks the same rules (receiving.service); these only make the screens explain them.
import React, { useRef, useState } from 'react';
import { Button, message } from 'antd';
import { CameraOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { PaymentMethod, ProofFile, RejectOutcome, uploadReceiptProof } from '../../api';

export const MAX_DAYS_BACK = 7;     // g06
export const REASON_AFTER_DAYS = 2; // g06

export const PAYMENT_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'on_account', label: 'On account' },
];
export const PAYMENT_LABEL: Record<string, string> = Object.fromEntries(PAYMENT_OPTIONS.map((o) => [o.value, o.label]));

export const REJECT_REASONS = [
  { value: 'wrong_item', label: 'Wrong item' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'short', label: 'Short' },
  { value: 'quality', label: 'Quality' },
  { value: 'other', label: 'Other' },
];

export const OUTCOME_OPTIONS: Array<{ value: RejectOutcome; label: string; hint: string }> = [
  { value: 'return', label: 'Return to vendor', hint: 'Opens a return (RET-); credit or refund is settled in Returns.' },
  { value: 'replacement', label: 'Vendor replaces it', hint: 'Stays open in Expected deliveries until the due date.' },
  { value: 'discount', label: 'Keep at a discount', hint: 'The goods are kept for an agreed lower amount.' },
  { value: 'writeoff', label: 'Write off', hint: 'Nothing comes back. Needs Ahmed\'s approval.' },
];
export const OUTCOME_LABEL: Record<string, string> = Object.fromEntries(OUTCOME_OPTIONS.map((o) => [o.value, o.label]));

export const PATH_LABEL: Record<string, string> = { direct: 'Direct purchase', order: 'Vendor order' };

/** Days a chosen date lies before today (0 = today). */
export const daysBack = (d: Dayjs | null) => (d ? dayjs().startOf('day').diff(d.startOf('day'), 'day') : 0);
/** DatePicker: today and up to 7 days back. */
export const outsideReceiptWindow = (d: Dayjs) => d.isAfter(dayjs(), 'day') || d.isBefore(dayjs().subtract(MAX_DAYS_BACK, 'day'), 'day');
export const isoDay = (d: Dayjs | null) => (d ? d.format('YYYY-MM-DD') : '');
export const money = (n: number, decimals = 2) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/** Receipt / delivery note photos: uploaded as soon as they are picked (private storage), listed with a remove button. */
export const ProofPicker: React.FC<{ value: ProofFile[]; onChange: (files: ProofFile[]) => void; label?: string }> = ({ value, onChange, label }) => {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (list: FileList | null) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const up = await uploadReceiptProof(files);
      onChange([...value, ...up]);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not add the file');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value.map((f, i) => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #b7eb8f', background: '#f6ffed', borderRadius: 8, padding: '6px 10px' }}>
          <FileOutlined style={{ color: '#389e0d' }} aria-hidden />
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
          <span style={{ fontSize: 12, color: '#5f6672' }}>{Math.max(1, Math.round(f.size / 1024))} KB</span>
          <Button type="text" size="small" icon={<DeleteOutlined />} aria-label={`Remove ${f.name}`} onClick={() => onChange(value.filter((_, j) => j !== i))} />
        </div>
      ))}
      {/* No capture attribute: phones then offer both the camera and photos already taken. */}
      <input ref={input} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => pick(e.target.files)} />
      <Button icon={<CameraOutlined />} loading={busy} onClick={() => input.current?.click()} style={{ alignSelf: 'flex-start' }}>
        {value.length ? 'Add another' : label || 'Add a photo'}
      </Button>
    </div>
  );
};
